import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { useLocalization } from '@shared-ui/localization-context';

//components
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { Typeahead, DatePicker, TimePicker } from 'src/components/common/modules/form';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';

//types
import { GlobalNavigationLineOfBusiness } from 'src/common/__generated__/api/types';
import { UitkButtonSize } from 'uitk-react-button';

//helpers
import { checkValue } from 'src/components/common/helper';
import { useStores } from 'src/utils/useStore';
import gaLogger from 'src/utils/ga-logger';
import {
  getWizardUrlOverrideURL,
  formatDateWithLang,
  getWizOverrideDateParaName,
  linkTabOpenner,
  renderInvalidTypeheadStyles,
  getWizardsOverrides,
  wizardDate,
  getValidReturnDate,
  getLocalizationData, 
  getWizardDefaults, 
  WizardDefaultProps
} from '../../helpers';
import { sendSearchWizardSearchGTMEvent } from 'src/components/common/utils/gtmHelperFunctions';

export const Cars: React.FC<any> = ({ localizationData, device, data, mccidData }) => {
  //access the context
  const { context } = useStores();

  const isHotelsCom = context?.site?.brand === 'hotels';

  const { formatDateString } = useLocalization();

  const today = new Date();
  const dayAfterTwoWeeks = new Date();
  const nextDayAfterDayAfterTwoWeeks = new Date();
  dayAfterTwoWeeks.setDate(today.getDate() + 14);
  nextDayAfterDayAfterTwoWeeks.setDate(today.getDate() + 15);

  // Check the default data is coming from the searchOverride if so
  // Get the code
  const wizardDefault: WizardDefaultProps = {
    data,
    lob: 'Car',
    overrideController: 'returnDefault',
  };
  const wizardOverrideDefault = getWizardDefaults(wizardDefault);
  let returnDefault = '';
  if (checkValue(wizardOverrideDefault, true)) {
    const getReturn = wizardOverrideDefault?.find((w) => w.key === 'returnDefault');
    if (checkValue(getReturn) && checkValue(getReturn.value)) {
      returnDefault = getReturn?.value;
    }
  } else {
    returnDefault = get(data, 'returnDefault', '');
  }

  // Get Wizard Overrides Date
  const wizardOverrides = getWizardsOverrides(data, 'Car');
  let wizardOverridesDepartureDate = '';
  let wizardOverridesReturnDate = '';
  if (checkValue(wizardOverrides, true)) {
    wizardOverridesDepartureDate = wizardOverrides?.[0]?.dateFrom;
    wizardOverridesReturnDate = wizardOverrides?.[0]?.dateTo;
  }
  const departureDate = wizardDate(
    get(data, 'departureDate', ''),
    get(data, 'departureDateFixed', ''),
    wizardOverridesDepartureDate,
    dayAfterTwoWeeks,
    get(data, 'daysInFuture', ''),
    ''
  );
  const returnDatewizardDate = wizardDate(
    get(data, 'returnDate', ''),
    get(data, 'returnDateFixed', ''),
    wizardOverridesReturnDate,
    nextDayAfterDayAfterTwoWeeks,
    get(data, 'daysInFuture', ''),
    get(data, 'lengthOfStay', '')
  );
  const returnDate = getValidReturnDate(departureDate, returnDatewizardDate);

  //init state
  const initFormState = {
    carPickup: { value: get(data, 'departureDefault', ''), error: '', typeHeadId: 'carPickupWizard' },
    carDropOff: { value: returnDefault, error: '', typeHeadId: 'carDropOffWizard' },
    pickUpDate: {
      value: departureDate,
      error: '',
    },
    dropOffDate: {
      value: returnDate,
      error: '',
    },
    pickUpTime: { value: '1030AM', error: '' },
    dropOffTime: { value: '1030AM', error: '' },
  };

  //formstate handle
  const [formState, setFormState] = useState(initFormState);

  //onChangeHandler
  const onChangeHandler = (state, value) => {
    setFormState((prev) => ({
      ...prev,
      [state]: {
        ...prev?.[state],
        value,
        error: '',
      },
    }));
  };

  //handle validations
  const handleValidations = () => {
    let isValidate = true;
    let pickupError = '';
    let dropOffError = '';

    if (formState?.carPickup?.value === '') {
      isValidate = false;
      pickupError = getLocalizationData(localizationData, 'select_origin', 'Please select an origin', device);
    }

    if (formState?.carDropOff?.value === '') {
      isValidate = false;
      dropOffError = getLocalizationData(localizationData, 'select_destination', 'Please select a destination', device);
    }

    setFormState((prev) => ({
      ...prev,
      carPickup: { ...prev?.carPickup, error: pickupError },
      carDropOff: { ...prev?.carDropOff, error: dropOffError },
    }));

    return isValidate;
  };

  //search button onClikc
  const handleSubmit = () => {
    const pickupDateFormatHotels = formatDateWithLang(context?.locale, formState?.pickUpDate?.value, 'yyyy-MM-dd');
    const dropoffDateFormatHotels = formatDateWithLang(context?.locale, formState?.dropOffDate?.value, 'yyyy-MM-dd');

    const pickupDateFormat = formatDateWithLang(context?.locale, formState?.pickUpDate?.value);
    const dropoffDateFormat = formatDateWithLang(context?.locale, formState?.dropOffDate?.value);

    if (handleValidations()) {
      let searchURL = '';
      if (isHotelsCom) {
        searchURL =
          getWizardUrlOverrideURL('Car', 'https://cars.hotels.com/en-us/book', data?.wizardUrlOverride) +
          '?pickupDateTime=' +
          formatDateString(pickupDateFormatHotels?.date, { raw: pickupDateFormatHotels?.dateFormats }) +
          'T' +
          formState?.pickUpTime?.value.slice(0, 2) +
          ':' +
          formState?.pickUpTime?.value.slice(2, 4) +
          '&returnDateTime=' +
          formatDateString(dropoffDateFormatHotels?.date, { raw: dropoffDateFormatHotels?.dateFormats }) +
          'T' +
          formState?.dropOffTime?.value.slice(0, 2) +
          ':' +
          formState?.dropOffTime?.value.slice(2, 4) +
          '&pickupName=' +
          formState?.carPickup?.value +
          '&returnName=' +
          formState?.carDropOff?.value +
          '#/vehicles';
      } else {
        searchURL =
          getWizardUrlOverrideURL('Car', '/carsearch?', data?.wizardUrlOverride) +
          'locn=' +
          formState?.carPickup?.value +
          '&loc2=' +
          formState?.carDropOff?.value +
          '&' +
          getWizOverrideDateParaName('Car', 'dateFrom', 'DATEFROM', 'date1', data?.wizardUrlOverride) +
          '=' +
          formatDateString(pickupDateFormat?.date, { raw: pickupDateFormat?.dateFormats }) +
          '&time1=' +
          formState?.pickUpTime?.value +
          '&' +
          getWizOverrideDateParaName('Car', 'dateTo', 'DATETO', 'date2', data?.wizardUrlOverride) +
          '=' +
          formatDateString(dropoffDateFormat?.date, { raw: dropoffDateFormat?.dateFormats }) +
          '&time2=' +
          formState?.dropOffTime?.value;
      }
      const searchParameters =
        formatDateString(formState?.pickUpDate?.value, { skeleton: 'MMMd' }) +
        '-' +
        formatDateString(formState?.dropOffDate?.value, { skeleton: 'MMMd' }) +
        ' ' +
        formState?.carPickup?.value +
        '-' +
        formState?.carDropOff?.value;
      gaLogger.logGa('wizard search', 'car tab', searchParameters);

      sendSearchWizardSearchGTMEvent('wizard_search', 'Car', searchParameters , searchURL);

      linkTabOpenner(searchURL, mccidData);
    }
  };

  //search button props
  const primaryButtonProps: PrimaryButtonProps = {
    text: getLocalizationData(localizationData, 'search_button_copy', 'Search', device),
    tag: 'button',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'Featured Content',
      eventLabel: `${getLocalizationData(localizationData, 'search_button_copy', 'Search', device)} - ${'cTALink'}`,
    },
    exClass: `btn mobile-btn`,
    target: '_blank',
    size: UitkButtonSize.LARGE,
    onClickEvent: () => handleSubmit(),
  };

  //typehead error state styles add
  useEffect(() => {
    const errors = Object.keys(formState)
      .map((key) => ({
        error: get(formState, `[${key}].error`, ''),
        typeheadId: get(formState, `[${key}].typeHeadId`, ''),
      }))
      .filter(({ typeheadId }) => typeheadId !== '' && typeheadId !== false && typeheadId !== undefined);

    renderInvalidTypeheadStyles(errors);
  }, [formState]);

  return (
    <>
      <div className="car-wrapper" data-testid="car-search-form">
        <UitkLayoutGrid columns={{ small: 1, medium: 2 }} space="three" className="wizard-row-top-margin">
          <UitkLayoutGridItem>
            <div>
              <Typeahead
                controlid={formState?.carPickup?.typeHeadId}
                label={getLocalizationData(localizationData, 'pick_location_label_copy', 'Pick-up', device)}
                icon={'place'}
                name={'carPickup'}
                controller={'typeahead'}
                updateModel={(selectedValue) => onChangeHandler('carPickup', selectedValue)}
                placeholder={getLocalizationData(localizationData, 'pick_location_placeholder_copy', 'Pick-up', device)}
                value={formState?.carPickup?.value}
                hasClearButton={false}
                lob={GlobalNavigationLineOfBusiness.HOTEL}
                error={formState?.carPickup?.error}
              />
            </div>
          </UitkLayoutGridItem>
          <UitkLayoutGridItem>
            <div>
              <Typeahead
                controlid={formState?.carDropOff?.typeHeadId}
                label={getLocalizationData(localizationData, 'drop_location_label_copy', 'Drop-off', device)}
                icon={'place'}
                name={'carDropOff'}
                controller={'typeahead'}
                updateModel={(selectedValue) => onChangeHandler('carDropOff', selectedValue)}
                placeholder={getLocalizationData(
                  localizationData,
                  'drop_location_placeholder_copy',
                  'Drop-off',
                  device
                )}
                value={formState?.carDropOff?.value}
                hasClearButton={false}
                lob={GlobalNavigationLineOfBusiness.HOTEL}
                error={formState?.carDropOff?.error}
              />
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
        <UitkLayoutGrid className="wizard-row-top-margin" columns={{ small: 1, large: 2 }} space="three">
          <UitkLayoutGridItem>
            <div>
              <DatePicker
                updateDates={(startDate, endDate, startDateState, endDateState) => {
                  onChangeHandler(startDateState, formatDateString(startDate, { raw: 'yyyy-MM-dd' }));
                  onChangeHandler(endDateState, formatDateString(endDate, { raw: 'yyyy-MM-dd' }));
                }}
                startDate={formState?.pickUpDate?.value}
                endDate={formState?.dropOffDate?.value}
                startPlaceHolder={getLocalizationData(localizationData, 'pick_date_label_copy', 'Pick-up date', device)}
                endPlaceHolder={getLocalizationData(localizationData, 'drop_date_label_copy', 'Drop-off date', device)}
                startDateState={'pickUpDate'}
                endDateState={'dropOffDate'}
                singleMode={false}
                doubleMonths={true}
                selectCopy={getLocalizationData(localizationData, 'date_picker_select', 'Select Date', device)}
                startLabel={getLocalizationData(localizationData, 'pick_date_label_copy', 'Pick-up date', device)}
                endLabel={getLocalizationData(localizationData, 'drop_date_label_copy', 'Drop-off date', device)}
                singleDateSelect={true}
              />
            </div>
          </UitkLayoutGridItem>
          <UitkLayoutGridItem>
            <div>
              <UitkLayoutGrid columns={{ small: 2 }} space="three">
                <UitkLayoutGridItem>
                  <div>
                    <TimePicker
                      label={getLocalizationData(localizationData, 'pick_up_time_label_copy', 'Pick-up time', device)}
                      icon="access_time"
                      name="pickUpTime"
                      placeholder={getLocalizationData(
                        localizationData,
                        'pick_up_time_label_copy',
                        'Pick-up time',
                        device
                      )}
                      onChange={(e) => onChangeHandler('pickUpTime', e?.target?.value)}
                      value={formState?.pickUpTime?.value}
                      error={formState?.pickUpTime?.error}
                    />
                  </div>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem>
                  <div>
                    <TimePicker
                      label={getLocalizationData(localizationData, 'drop_off_time_label_copy', 'Drop-off time', device)}
                      icon="access_time"
                      name="dropOffTime"
                      placeholder={getLocalizationData(
                        localizationData,
                        'drop_off_time_label_copy',
                        'Drop-off time',
                        device
                      )}
                      onChange={(e) => onChangeHandler('dropOffTime', e?.target?.value)}
                      value={formState?.dropOffTime?.value}
                      error={formState?.dropOffTime?.error}
                    />
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
        <div className="wizard-search-button-container">
          <PrimaryButton {...primaryButtonProps} />
        </div>
      </div>
    </>
  );
};
